import { AutoRefreshLoginServiceClient } from "./auth/login/AutoRefreshLoginServiceClient.js";
import { LoginServiceClient } from "./auth/login/LoginServiceClient.js";
import { GroupsServiceClient } from "./auth/users/GroupsServiceClient/GroupsServiceClient.js";
import { UsersServiceClient } from "./auth/users/UsersServiceClient/UsersServiceClient.js";
import {
  UserDataServiceClient,
  VectorTileServiceClient,
} from "./tracking/index.js";

export interface OneMapCloudClientConfig {
  /**
   * A string to prepend to every API call, e.g. `https://dev.onemap.earth`.
   *
   * Not necessary in a web app where the UI is served from the same domain as the API —
   * calls then go to a relative path under `/api/`, which API Gateway maps to the same
   * routes.
   */
  baseUri?: string;

  /**
   * Wrap the underlying `LoginServiceClient` in `AutoRefreshLoginServiceClient`, which
   * schedules background token refreshes before the access token expires. Only valid in
   * a browser context (depends on `window.localStorage`).
   *
   * Defaults to `false` — callers in Node-only contexts (tests, scripts) keep raw login
   * control.
   */
  autoRefreshLogin?: boolean;
}

/**
 * Unified entrypoint for OneMap cloud services.
 *
 * OneMap uses HTTP-only cookies for authentication, so once `client.login.login(...)`
 * succeeds the browser automatically forwards credentials on subsequent requests. There
 * is no in-memory bearer-token management here, unlike `RescueallyCloudClient`.
 */
export class OneMapCloudClient {
  public readonly login: LoginServiceClient | AutoRefreshLoginServiceClient;
  public readonly users: UsersServiceClient;
  public readonly groups: GroupsServiceClient;
  public readonly tracking: UserDataServiceClient;
  public readonly vectorTiles: VectorTileServiceClient;

  constructor(config: OneMapCloudClientConfig = {}) {
    const baseUri = config.baseUri ?? "";
    const baseLogin = new LoginServiceClient({ baseUri });
    this.login = config.autoRefreshLogin
      ? new AutoRefreshLoginServiceClient(baseLogin)
      : baseLogin;
    this.users = new UsersServiceClient({ baseUri });
    this.groups = new GroupsServiceClient({ baseUri });
    this.tracking = new UserDataServiceClient({ baseUri });
    this.vectorTiles = new VectorTileServiceClient({ baseUri });
  }
}
